// webapp/src/lib/diag/messageTap.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
let _installed = false;

export interface MessageTapOptions {
  minDurationMs?: number;              // default 32
  includeRegisterStack?: boolean;      // default true
  includeInvokeStack?: boolean;        // default false (expensive)
}

function classifyData(d: any): string {
  if (d == null) return 'nullish';
  const t = typeof d;
  if (t !== 'object') return t;
  if (Array.isArray(d)) return 'array';
  const k = (d.type || d.event || d.kind || d.action || d.msg || d.message);
  return k ? `obj:${String(k)}` : 'obj';
}

export function installMessageTap(opts: MessageTapOptions = {}): void {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  const min = Math.max(0, opts.minDurationMs ?? 32);
  const captureRegStack = opts.includeRegisterStack ?? true;
  const captureInvokeStack = opts.includeInvokeStack ?? false;

  const _add = window.addEventListener.bind(window);
  const _remove = window.removeEventListener.bind(window);
  const regInfo = new WeakMap<EventListener, { stack?: string }>();
  const wrapperMap = new WeakMap<EventListener, EventListener>();

  window.addEventListener = function patchedAddEventListener(
    type: any,
    listener: any,
    options?: any
  ) {
    if (type === 'message' && typeof listener === 'function') {
      const info: any = {};
      if (captureRegStack) {
        info.stack = (new Error().stack || '').split('\n').slice(2, 10).join('\n');
      }
      regInfo.set(listener, info);

      const wrapped: EventListener = function (this: any, event: Event) {
        const ev = event as MessageEvent;
        const t0 = performance.now();
        try {
          return (listener as any).call(this, ev);
        } finally {
          const dt = performance.now() - t0;
          if (dt >= min) {
            // eslint-disable-next-line no-console
            console.warn('[msgtap] slow message handler', {
              ms: Math.round(dt),
              origin: ev.origin,
              dataType: typeof ev.data,
              dataKind: classifyData(ev.data),
              registeredAt: regInfo.get(listener)?.stack,
              invokeStack: captureInvokeStack
                ? (new Error().stack || '').split('\n').slice(2, 10).join('\n')
                : undefined,
            });
          }
        }
      };
      try { (wrapped as any).__orig = listener; } catch {}
      wrapperMap.set(listener, wrapped);
      return _add(type, wrapped as EventListener, options as any);
    }
    return _add(type, listener as any, options as any);
  } as any;

  window.removeEventListener = function patchedRemoveEventListener(
    type: any,
    listener: any,
    options?: any
  ) {
    if (type === 'message' && typeof listener === 'function') {
      const wrapped = (wrapperMap.get(listener) || listener) as EventListener;
      return _remove(type, wrapped, options as any);
    }
    return _remove(type, listener as any, options as any);
  } as any;

  // Also patch window.onmessage direct assignment
  const desc = Object.getOwnPropertyDescriptor(window, 'onmessage');
  if (!desc || desc.configurable) {
    let handler: ((e: MessageEvent) => any) | null = null;
    Object.defineProperty(window, 'onmessage', {
      configurable: true,
      get() { return handler; },
      set(v: any) {
        if (typeof v !== 'function') { handler = v; return; }
        const regStack = captureRegStack ? (new Error().stack || '').split('\n').slice(2, 10).join('\n') : undefined;
        handler = function (e: MessageEvent) {
          const t0 = performance.now();
          try { return v(e); }
          finally {
            const dt = performance.now() - t0;
            if (dt >= min) {
              // eslint-disable-next-line no-console
              console.warn('[msgtap] slow window.onmessage', {
                ms: Math.round(dt),
                origin: e.origin,
                dataKind: classifyData(e.data),
                registeredAt: regStack,
                invokeStack: captureInvokeStack
                  ? (new Error().stack || '').split('\n').slice(2, 10).join('\n')
                  : undefined,
              });
            }
          }
        };
      }
    });
  }
}
